import camelCase from 'lodash/camelCase';
import type { CSSProperties } from 'react';

// The emotion route into the ramp. Values live in `@env0/ui-theme/typography.css`, which declares both
// the classes and the `--type-*` properties read here.

const RAMP = {
  headingH1: 'heading-h1',
  headingH2: 'heading-h2',
  headingH3: 'heading-h3',
  headingH4: 'heading-h4',
  bodyLg: 'body-lg',
  bodyMd: 'body-md',
  bodySm: 'body-sm',
  bodyLgBold: 'body-lg-bold',
  bodyMdBold: 'body-md-bold',
  bodySmBold: 'body-sm-bold',
  labelMd: 'label-md',
  labelSm: 'label-sm',
  overline: 'overline',
  captionMd: 'caption-md',
  captionSm: 'caption-sm',
  code: 'code'
} as const;

export type TypeStyle = keyof typeof RAMP;

/**
 * For sites that set only the family and keep their own size; prefer `typeStyle.code` for new work.
 *
 * The `var()` carries its own fallback: an undefined custom property makes the whole `font-family`
 * declaration invalid at computed-value time, which would inherit a proportional family rather than
 * fall through to the stack behind it.
 */
export const MONO_FONT_FAMILY =
  "var(--font-family-secondary, ui-monospace), ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace";

// Kept in step with typography.css; the spec fails if the two drift.
const EXTRAS: Partial<Record<TypeStyle, string>> = {
  overline: 'letter-spacing: 0.02em;\ntext-transform: uppercase;',
  code: `font-family: ${MONO_FONT_FAMILY};`
};

const declarations = (style: TypeStyle): string => {
  const slug = RAMP[style];
  const extras = EXTRAS[style];

  return [
    // Goes first so `code` can override it from EXTRAS.
    'font-family: var(--font-family-primary), sans-serif;',
    `font-size: var(--type-${slug}-size);`,
    `line-height: var(--type-${slug}-line);`,
    `font-weight: var(--type-${slug}-weight);`,
    ...(extras ? [extras] : [])
  ].join('\n');
};

const styles = Object.keys(RAMP) as TypeStyle[];

/** CSS declarations per ramp style, for interpolation into a styled component. */
export const typeStyle = Object.fromEntries(styles.map(style => [style, declarations(style)])) as Record<
  TypeStyle,
  string
>;

/** The matching class names, for elements emotion cannot reach. */
export const typeClass = Object.fromEntries(styles.map(style => [style, `type-${RAMP[style]}`])) as Record<
  TypeStyle,
  string
>;

// A later declaration wins, as in CSS: the code role's family replaces the primary one.
const toStyleObject = (css: string): CSSProperties =>
  Object.fromEntries(
    css.split('\n').map(declaration => {
      const [property, ...value] = declaration.replace(/;$/, '').split(':');
      return [camelCase(property), value.join(':').trim()];
    })
  );

/** `typeStyle` as style objects, for vendor APIs that set fonts inline, such as prism themes. */
export const typeProperties = Object.fromEntries(
  styles.map(style => [style, toStyleObject(typeStyle[style])])
) as Record<TypeStyle, CSSProperties>;

export const TYPE_STYLES = styles;
